import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import Icon from './Icon';
import Sheet from './Sheet';
import { PRIMARY } from './CaptureSheets';
import FoodPreferencesFields from './FoodPreferencesFields';
import { ProBadge, TrialTerms } from './ProSheet';
import { useTrialOfferShown } from './Trial';
import { useApp } from '../lib/AppContext';
import { isCoachPath } from '../lib/mode';

const FIELD = 'input h-12 w-full rounded-[14px] border-line text-base';
const TRAINING = [{ id: '0', label: '0' }, { id: '1-2', label: '1–2' }, { id: '3-4', label: '3–4' }, { id: '5+', label: '5+' }];
const GOALS = [
  { id: 'lose_fat', label: 'Giảm mỡ', text: 'giảm cân' },
  { id: 'maintain', label: 'Giữ dáng', text: 'giữ cân' },
  { id: 'gain_muscle', label: 'Tăng cơ', text: 'tăng cân nhẹ' },
];
const TARGETS = [
  { key: 'calorie_goal', label: 'Calo mỗi ngày', unit: 'kcal' },
  { key: 'protein_goal', label: 'Đạm ngày nghỉ', unit: 'g' },
  { key: 'workout_protein_goal', label: 'Đạm ngày tập', unit: 'g' },
];
// A student opening a coach's invite or code finishes that first; onboarding comes right after.
const JOINING = /^\/(me\/)?(i|c)\//;
const option = (on) => `min-h-12 rounded-[14px] border-2 bg-base-100 px-3 text-[15px] font-semibold ${on ? 'border-[#17804f] text-[#17804f]' : 'border-transparent'}`;

// `step` null: a screen after the three steps (the trial offer), without the step count.
function Frame({ step, title, text, onSkip, children }) {
  return (
    <div data-theme="macro" role="dialog" aria-modal="true" aria-labelledby="onboarding-title"
      className="fixed inset-0 z-50 overflow-y-auto bg-base-200 text-base-content">
      <div className="pt-safe pb-safe mx-auto flex min-h-dvh max-w-xl flex-col gap-4 px-4 pb-4">
        {step ? (
          <>
            <div className="flex min-h-11 items-center justify-between pt-4">
              <p className="text-xs font-bold uppercase tracking-wide text-muted">Bước {step}/3</p>
              {onSkip && <button type="button" className="min-h-11 px-1 text-sm font-bold text-brand-text" onClick={onSkip}>Bỏ qua</button>}
            </div>
            <div className="grid grid-cols-3 gap-1.5" aria-hidden="true">
              {[1, 2, 3].map((n) => <span key={n} className={`h-1 rounded-full ${n <= step ? 'bg-[#17804f]' : 'bg-line'}`} />)}
            </div>
          </>
        ) : <div className="pt-8" />}
        <div>
          <h1 id="onboarding-title" className="text-[26px] font-extrabold leading-tight">{title}</h1>
          <p className="text-sm text-muted">{text}</p>
        </div>
        {children}
      </div>
    </div>
  );
}

function Footer({ busy, disabled, error, label = 'Tiếp tục', note }) {
  return (
    <div className="mt-auto flex flex-col gap-2 pt-4">
      {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
      <button type="submit" className={PRIMARY} disabled={busy || disabled}>
        {busy && <span className="loading loading-spinner loading-sm" />} {label}
      </button>
      {note && <p className="text-center text-xs text-muted">{note}</p>}
    </div>
  );
}

// U1 "Giới thiệu một chút về bạn": everything the targets are worked out from, on one screen.
function AboutStep({ state, save }) {
  const [form, setForm] = useState(() => ({
    name: state.about.name || '', sex: state.about.sex || '', birth_year: state.about.birth_year || '',
    height_cm: state.about.height_cm || '', weight_kg: state.about.weight_kg || '', training: state.about.training || '',
  }));
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const ready = form.name.trim() && form.sex && form.birth_year && form.height_cm && form.weight_kg && form.training;
  const numbers = [
    { key: 'birth_year', label: 'Năm sinh', placeholder: '1996' },
    { key: 'height_cm', label: 'Chiều cao', placeholder: '170', unit: 'cm' },
    { key: 'weight_kg', label: 'Cân nặng', placeholder: '65', unit: 'kg', decimal: true },
  ];
  return (
    <Frame step={1} title="Giới thiệu một chút về bạn" text="Dùng để tính lượng calo và đạm phù hợp. Chỉ bạn thấy.">
      <form className="flex flex-1 flex-col gap-4" onSubmit={(e) => { e.preventDefault(); save(1, form); }}>
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold">Tên của bạn</span>
          <input className={FIELD} autoComplete="name" maxLength={50} placeholder="VD: Minh Anh" value={form.name} onChange={(e) => set('name')(e.target.value)} />
        </label>
        <div className="flex flex-col gap-1.5">
          <span id="sex-label" className="text-sm font-semibold">Giới tính</span>
          <div role="radiogroup" aria-labelledby="sex-label" className="grid grid-cols-2 gap-2">
            {[['male', 'Nam'], ['female', 'Nữ']].map(([id, label]) => (
              <button key={id} type="button" role="radio" aria-checked={form.sex === id} className={option(form.sex === id)} onClick={() => set('sex')(id)}>{label}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {numbers.map((n) => (
            <label key={n.key} className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold">{n.label}</span>
              <span className={`${FIELD} flex items-center gap-1 px-3`}>
                <input inputMode={n.decimal ? 'decimal' : 'numeric'} aria-label={n.label} placeholder={n.placeholder} className="tabular min-w-0 flex-1 bg-transparent outline-none"
                  value={form[n.key]} onChange={(e) => set(n.key)(e.target.value.replace(',', '.').replace(n.decimal ? /[^\d.]/g : /\D/g, ''))} />
                {n.unit && <span className="text-xs text-muted">{n.unit}</span>}
              </span>
            </label>
          ))}
        </div>
        <div className="flex flex-col gap-1.5">
          <span id="training-label" className="text-sm font-semibold">Bạn tập bao nhiêu buổi mỗi tuần?</span>
          <div role="radiogroup" aria-labelledby="training-label" className="grid grid-cols-4 gap-2">
            {TRAINING.map((t) => (
              <button key={t.id} type="button" role="radio" aria-checked={form.training === t.id} className={option(form.training === t.id)}
                onClick={() => set('training')(t.id)}>{t.label}</button>
            ))}
          </div>
        </div>
        <Footer busy={state.busy} error={state.error} disabled={!ready} />
      </form>
    </Frame>
  );
}

function HowSheet({ open, onClose }) {
  return (
    <Sheet open={open} onClose={onClose} title="Cách tính">
      <div className="flex flex-col gap-3 text-sm">
        <p>Calo: năng lượng cơ thể cần khi nghỉ (công thức Mifflin–St Jeor, theo giới tính, tuổi, chiều cao, cân nặng) nhân với mức vận động theo số buổi tập mỗi tuần.</p>
        <p>Giảm mỡ ăn khoảng 80% mức đó (giảm chừng 0,5 kg mỗi tuần), Giữ dáng 100%, Tăng cơ 110%.</p>
        <p>Đạm: 1,6–2,0 g cho mỗi kg cân nặng, ngày tập cao hơn ngày nghỉ.</p>
        <p className="text-muted">Đây là điểm bắt đầu. Bạn sửa được từng con số, bây giờ hoặc sau này trong Cài đặt.</p>
      </div>
    </Sheet>
  );
}

// U2 "Mục tiêu của bạn": pick a direction, the app fills in the numbers, and they stay editable. A student whose
// coach set targets sees those, locked: only the coach changes them.
function TargetsStep({ state, save }) {
  const locked = state.locked_by;
  const [goal, setGoal] = useState('maintain');
  // A coach who set no training-day protein means the rest-day one counts on training days too (UserTargets).
  const [values, setValues] = useState(() => (locked
    ? { ...state.targets, workout_protein_goal: state.targets.workout_protein_goal ?? state.targets.protein_goal }
    : state.suggestions?.maintain || state.targets));
  const [how, setHow] = useState(false);
  const pick = (id) => { setGoal(id); if (state.suggestions?.[id]) setValues(state.suggestions[id]); };
  const ready = locked || TARGETS.every((t) => Number(values[t.key]) > 0);

  return (
    <Frame step={2} title="Mục tiêu của bạn"
      text={locked ? `Coach ${locked.name} đã đặt mục tiêu khi mời bạn.` : 'Chọn hướng đi, app gợi ý con số. Bạn sửa được.'}>
      <form className="flex flex-1 flex-col gap-4" onSubmit={(e) => { e.preventDefault(); save(2, { goal, ...values }); }}>
        {locked ? (
          <section data-theme="macro-dark" className="flex items-center gap-3 rounded-[16px] bg-base-200 p-4 text-base-content">
            <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[#6fd3a0] font-bold text-[#15261f]">{(locked.name || 'C').trim()[0].toUpperCase()}</span>
            <span className="min-w-0">
              <span className="block font-bold">Mục tiêu do Coach {locked.name} đặt</span>
              {locked.gym && <span className="block text-xs text-muted">PT tại {locked.gym}</span>}
            </span>
          </section>
        ) : (
          <>
            <div role="radiogroup" aria-label="Hướng đi" className="grid grid-cols-3 gap-2">
              {GOALS.map((g) => (
                <button key={g.id} type="button" role="radio" aria-checked={goal === g.id} onClick={() => pick(g.id)}
                  className={`flex min-h-16 flex-col justify-center rounded-[14px] border-2 bg-base-100 px-3 text-left ${goal === g.id ? 'border-[#17804f]' : 'border-transparent'}`}>
                  <span className="font-bold">{g.label}</span>
                  <span className="text-xs text-muted">{g.text}</span>
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wide text-muted">Gợi ý cho bạn</p>
              <button type="button" className="min-h-11 px-1 text-sm font-bold text-brand-text" onClick={() => setHow(true)}>Cách tính</button>
            </div>
          </>
        )}
        <section className="divide-y divide-line rounded-[20px] bg-base-100 px-4">
          {TARGETS.map((t) => (
            <label key={t.key} className="flex min-h-16 items-center justify-between gap-3">
              <span className="text-[15px] font-semibold">{t.label}</span>
              <span className="flex items-center gap-2">
                <span className={`flex h-11 w-28 items-center justify-end gap-1.5 rounded-[12px] border px-3 ${locked ? 'border-transparent bg-base-200' : 'border-line'}`}>
                  {locked && <Icon name="lock" className="size-3.5 text-muted" />}
                  <input inputMode="numeric" aria-label={t.label} readOnly={!!locked} className="tabular w-full min-w-0 bg-transparent text-right font-bold outline-none"
                    value={values?.[t.key] ?? ''} onChange={(e) => setValues((v) => ({ ...v, [t.key]: e.target.value.replace(/\D/g, '') }))} />
                </span>
                <span className="w-8 text-xs text-muted">{t.unit}</span>
              </span>
            </label>
          ))}
        </section>
        {locked ? (
          <p className="flex gap-2 rounded-[14px] bg-[#fdf3d7] p-3 text-sm text-[#5c4300]">
            <Icon name="lock" className="mt-0.5 size-4 shrink-0" />
            Chỉ Coach {locked.name} sửa được mục tiêu này. Muốn đổi, hãy trao đổi với Coach.
          </p>
        ) : (
          <p className="text-xs text-muted">Ngày có ghi buổi tập, app dùng mức đạm ngày tập và cộng calo đã đốt vào phần còn lại.</p>
        )}
        <Footer busy={state.busy} error={state.error} disabled={!ready} />
      </form>
      <HowSheet open={how} onClose={() => setHow(false)} />
    </Frame>
  );
}

// U3 "Vài điều nữa": Kiểu ăn, Món không ăn được and the meal reminder, for the AI's dishes and menus. Optional.
function ExtrasStep({ state, save }) {
  const [food, setFood] = useState({ diet: state.food.diet_style || 'all', avoid: state.food.avoid_foods || [] });
  const [meals, setMeals] = useState(true);
  return (
    <Frame step={3} title="Vài điều nữa" text="Giúp AI gợi ý món hợp với bạn. Không bắt buộc." onSkip={() => save(3, { skip: true })}>
      <form className="flex flex-1 flex-col gap-4"
        onSubmit={(e) => { e.preventDefault(); save(3, { diet_style: food.diet, avoid_foods: food.avoid, meals_reminder: meals }); }}>
        <FoodPreferencesFields diet={food.diet} avoid={food.avoid} onChange={setFood} />
        <div className="flex flex-col gap-2">
          <span className="text-sm font-semibold">Nhắc nhở</span>
          <label className="flex min-h-16 items-center justify-between gap-3 rounded-[20px] bg-base-100 px-4">
            <span>
              <span className="block text-[15px] font-semibold">Nhắc ghi bữa ăn</span>
              <span className="block text-xs text-muted">12:00 và 19:30</span>
            </span>
            <input type="checkbox" className="toggle toggle-primary" checked={meals} onChange={(e) => setMeals(e.target.checked)} />
          </label>
        </div>
        <Footer busy={state.busy} error={state.error} label="Bắt đầu dùng Macro Mate" note="Đổi lại bất cứ lúc nào trong Cài đặt." />
      </form>
    </Frame>
  );
}

const TRIAL_BENEFITS = [
  'Thực đơn tuần do AI lên theo mục tiêu, kèm danh sách đi chợ',
  'Đổi món trong thực đơn bằng AI',
  '20 lượt AI mỗi ngày để chụp và phân tích bữa ăn, buổi tập',
  'Không quảng cáo',
];

// After the three steps, for an account that hasn't tried Pro: the 7-day trial, with its terms said first (how long,
// no payment, what stops after, what Pro costs). It starts only on "Bắt đầu"; "Bắt đầu với Miễn phí" is as plain to
// see, and leaves it for after the first AI meal and Gói & lượt AI.
function TrialOfferStep({ membership, onStart, onLater, busy, error }) {
  useTrialOfferShown('onboarding', true);
  return (
    <Frame step={null} title="Tặng bạn 7 ngày Pro miễn phí" text="Dùng thử đầy đủ Macro Mate trước khi quyết định.">
      <div className="flex flex-1 flex-col gap-4">
        <section className="flex flex-col gap-2.5 rounded-[20px] bg-base-100 p-4">
          <p className="flex items-center gap-2 font-bold"><ProBadge /> Trong 7 ngày bạn có</p>
          <ul className="flex flex-col gap-2">
            {TRIAL_BENEFITS.map((b) => (
              <li key={b} className="flex gap-2.5 text-[15px]"><Icon name="check" className="mt-0.5 size-5 shrink-0 text-brand-text" /> {b}</li>
            ))}
          </ul>
        </section>
        <TrialTerms membership={membership} />
        <div className="mt-auto flex flex-col gap-2 pt-2">
          {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
          <button type="button" className={PRIMARY} disabled={busy} onClick={onStart}>
            {busy && <span className="loading loading-spinner loading-sm" />} Bắt đầu 7 ngày miễn phí
          </button>
          <button type="button" className="btn h-12 rounded-2xl border-line bg-base-100 font-semibold" disabled={busy} onClick={onLater}>
            Bắt đầu với Miễn phí
          </button>
        </div>
      </div>
    </Frame>
  );
}

// The onboarding after the role question: U1 → U2 → U3, each saved as it's done so the app carries on where it
// stopped; the app opens once U3 is done or skipped. Coach mode doesn't wait for it: a coach does it the
// first time they open the personal app.
export default function Onboarding({ base }) {
  const { api, profile, setProfile, setGoals, setReminders, setMembership } = useApp();
  const { pathname } = useLocation();
  const [state, setState] = useState(null);
  const after = pathname.startsWith(base) ? pathname.slice(base.length) : pathname;
  const waiting = !profile?.needsOnboarding || profile?.needsRole || JOINING.test(pathname) || isCoachPath(after) || after.startsWith('/clients/');

  useEffect(() => {
    if (waiting || state) return;
    api.get('/onboarding').then(setState).catch((e) => setState({ loadError: e.message }));
  }, [waiting, state, api]);
  if (waiting) return null;
  if (!state || state.loadError) {
    return (
      <Frame step={1} title="Giới thiệu một chút về bạn" text="">
        {state?.loadError
          ? <p role="alert" className="text-sm font-medium text-error">{state.loadError}</p>
          : <div className="skeleton h-64 rounded-[20px]" />}
      </Frame>
    );
  }

  const save = async (step, body) => {
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const data = await api.patch('/onboarding', { step, ...body });
      if (data.done) {
        const t = data.targets;
        setGoals({ calories: t.calorie_goal, protein_g: t.protein_goal, workout_protein_g: t.workout_protein_goal });
        setReminders(data.reminders);
        setMembership(data.membership);
        // Not tried Pro yet: the trial offer comes before the app opens.
        if (data.membership.trial_available) return setState({ ...data, offer: true });
        finish(data);
      }
      setState(data);
    } catch (e) {
      setState((s) => ({ ...s, busy: false, error: e.message }));
    }
  };

  function finish(data) {
    setProfile((p) => ({ ...p, needsOnboarding: false, needsName: false, name: data.about.name || p.name }));
  }
  // The trial offer: starting it, or "Bắt đầu với Miễn phí" (offered again only after the first AI meal), then the app opens.
  const answerOffer = async (start) => {
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      setMembership(await api.post(start ? '/membership/trial' : '/membership/prompted', { placement: 'onboarding' }));
      finish(state);
    } catch (e) {
      setState((s) => ({ ...s, busy: false, error: e.message }));
    }
  };

  if (state.offer) {
    return <TrialOfferStep membership={state.membership} busy={state.busy} error={state.error}
      onStart={() => answerOffer(true)} onLater={() => answerOffer(false)} />;
  }
  if (state.step === 1) return <AboutStep state={state} save={save} />;
  if (state.step === 2) return <TargetsStep key={state.suggestions ? 'ready' : 'empty'} state={state} save={save} />;
  return <ExtrasStep state={state} save={save} />;
}
